/* PatientCommandHeader — iter101.59
 *
 * Patient-profile header for the Doctor portal. Composes the shared
 * /components/shared/CommandHeaderShell so the visual grammar matches
 * the Doctor + Planner case headers. Patient-scoped, not case-scoped:
 *
 *   Zone A
 *     · avatar (with click-to-upload / replace / remove overlay)
 *     · caps kicker "Patient"
 *     · name + demographics (age · gender · INV-PT-…)
 *     · "Edit info" link in the demographics suffix
 *     · assignment mini-stack: Primary clinic + Contact phone
 *     · rightSlot: Book scan visit + New case CTAs
 *
 *   Zone B (patient-scoped chip strip — 6 cells)
 *     · Patient ID · Added · Total cases · Latest case status ·
 *       Quoted (₹) · Balance (₹ · rose when > 0)
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
    Buildings, Hash, CalendarBlank, Phone, Plus, CalendarPlus,
    Folders, HeartStraight, ReceiptX,
    // iter101.89 — Compliance chip now reads clinical adherence.
    CheckCircle, Bell, Warning,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import CommandHeaderShell from "@/components/shared/CommandHeaderShell";
import PatientAvatar from "@/components/patient/PatientAvatar";


function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
        }).replace(/\s/g, "-");
    } catch {
        return "—";
    }
}

function fmtINR(n) {
    if (typeof n !== "number") return "—";
    if (n === 0) return "₹0";
    return new Intl.NumberFormat("en-IN", {
        style: "currency", currency: "INR", maximumFractionDigits: 0,
    }).format(n);
}

/* Titlecase a snake_case case status like "in_treatment" → "In treatment". */
function humanCaseStatus(s) {
    if (!s) return "No case yet";
    return String(s).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

/* Rough sentiment mapping for the latest-case chip tone. */
function statusTone(s) {
    if (!s) return "slate";
    if (/completed|delivered|treatment_started|in_treatment/i.test(s)) return "emerald";
    if (/cancelled|rejected|blocked|failed/i.test(s)) return "rose";
    if (/pending|awaiting|review|revision/i.test(s)) return "amber";
    return "neutral";
}


export default function PatientCommandHeader({
    patient,
    photoInputRef,
    uploadingPhoto,
    onRemovePhoto,
    onBookScanVisit,
}) {
    const navigate = useNavigate();
    if (!patient) return null;

    const ageStr = patient.age ? `${patient.age} yr` : null;
    const genderStr = patient.gender
        ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1)
        : null;
    const idStr = patient.human_id
        || `INV-PT-${(patient.id || "").slice(0, 6).toUpperCase()}`;
    // iter101.89 — Patient ID dropped from the demographics line per
    // user direction. The ID still surfaces once in the chip strip
    // below (dedicated "Patient ID" cell) so the doctor can still
    // copy it when needed, but it no longer clutters the identity
    // band next to Age · Gender.
    const demographics = [ageStr, genderStr].filter(Boolean);

    /* iter101.89 — Patient ID sub-line under demographics dropped. The
       phone number surfaces on its own row (basis-full flex break) as
       the sole item in the demographicsSuffix now that the Edit Info
       link + INV-PT-… stripe are gone. */
    const contactChip = patient.phone
        ? (
            <>
                <span className="basis-full h-0 w-full" aria-hidden />
                <span
                    className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground"
                    data-testid="patient-header-contact-inline"
                >
                    <Phone size={11} weight="bold" className="text-muted-foreground/70" />
                    <a
                        href={`tel:${patient.phone.replace(/\s/g, "")}`}
                        className="hover:text-primary hover:underline tabular-nums"
                        title={`Call ${patient.name}`}
                    >
                        {patient.phone}
                    </a>
                </span>
            </>
        )
        : null;

    // iter101.89 — "Edit info" link removed from the header per user
    // direction. Doctors can still edit patient info via the About tab
    // on the profile page; the header stops advertising it.
    const demographicsSuffix = contactChip;

    /* Avatar — wraps the shared `PatientAvatar` in the clickable
       upload / replace / remove treatment the header owned before
       this refactor. Kept inside the shell's `avatar` slot so the
       shell doesn't need to know about upload flows. */
    const avatarNode = (
        <div className="relative group">
            <button
                type="button"
                onClick={() => photoInputRef?.current?.click()}
                className="relative rounded-full focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
                disabled={uploadingPhoto}
                title={patient.photo_data_url ? "Click to replace photo" : "Click to upload photo"}
                data-testid="patient-photo-upload-btn"
            >
                <PatientAvatar
                    patient={patient}
                    size="xl"
                    className="ring-1 ring-teal-200/60 ring-offset-2 ring-offset-card group-hover:ring-teal-400/60 transition"
                />
                <span className="absolute inset-0 rounded-full bg-black/55 grid place-items-center text-white text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    {uploadingPhoto ? "Saving…" : (patient.photo_data_url ? "Replace" : "Add photo")}
                </span>
            </button>
            {patient.photo_data_url && !uploadingPhoto && onRemovePhoto && (
                <button
                    type="button"
                    onClick={onRemovePhoto}
                    className="absolute -bottom-0.5 -right-0.5 w-6 h-6 rounded-full bg-card border border-border text-muted-foreground hover:text-rose-600 hover:border-rose-300 grid place-items-center opacity-0 group-hover:opacity-100 transition text-xs"
                    title="Remove photo"
                    data-testid="patient-photo-remove-btn"
                >
                    ×
                </button>
            )}
        </div>
    );

    /* Right slot — patient-level primary CTAs. Book scan visit is the
       high-margin funnel CTA (rose fill), New case is the primary
       treatment CTA (teal fill). */
    const rightSlot = (
        <div className="flex items-center gap-2 flex-wrap" data-testid="patient-header-actions">
            {onBookScanVisit && (
                <Button
                    size="sm"
                    onClick={onBookScanVisit}
                    className="rounded-full h-8 px-3.5 bg-rose-600 hover:bg-rose-700 text-white shadow-sm hover:shadow-md transition-shadow"
                    data-testid="patient-header-book-scan-btn"
                    title="Schedule a scanner visit for this patient"
                >
                    <CalendarPlus size={13} weight="bold" className="mr-1" /> Book scan visit
                </Button>
            )}
            <Button
                size="sm"
                onClick={() => navigate(`/doctor/cases/new?patient_id=${patient.id}`)}
                className="rounded-full h-8 px-3.5"
                data-testid="patient-header-new-case-btn"
                title="Start a new aligner case for this patient"
            >
                <Plus size={13} weight="bold" className="mr-1" /> New case
            </Button>
        </div>
    );

    /* Patient-scoped chip strip. Six cells, same physical rhythm as
       the case headers so the doctor's eye lands on the same rows. */
    const payments = patient.payments_summary || {};
    const casesCount = payments.cases_count || 0;
    const balance = payments.balance || 0;
    // iter101.88 — `quoted` used to feed the removed Quoted chip; the new
    // Compliance chip takes that slot. Kept the read commented for
    // history; can be revived if a Quoted chip is ever asked back.
    // const quoted = payments.quote_total || 0;
    const latestStatus = patient.latest_case_status || null;
    // iter101.89 — Clinical compliance status ("on_time" | "needs_attention"
    // | "at_risk" | null). Replaces the earlier iter101.88 boolean that
    // conflated compliance with CoE support mode.
    const complianceStatus = patient.latest_case_compliance;

    const complianceChip = (() => {
        if (complianceStatus === "on_time") {
            return {
                icon: CheckCircle,
                label: "Compliance",
                value: "On time",
                tone: "emerald",
                testid: "patient-chip-compliance-on-time",
                title: "7-day wear-time on target · logs fresh",
            };
        }
        if (complianceStatus === "needs_attention") {
            return {
                icon: Bell,
                label: "Compliance",
                value: "Needs attention",
                tone: "amber",
                testid: "patient-chip-compliance-needs-attention",
                title: "Wear-time slipping or logs going stale — nudge advised",
            };
        }
        if (complianceStatus === "at_risk") {
            return {
                icon: Warning,
                label: "Compliance",
                value: "At risk",
                tone: "rose",
                testid: "patient-chip-compliance-at-risk",
                title: "Low wear-time or patient has gone silent — reach out today",
            };
        }
        return {
            icon: CheckCircle,
            label: "Compliance",
            value: "—",
            tone: "slate",
            testid: "patient-chip-compliance-unset",
            title: "Compliance tracking starts once the patient begins wearing aligners",
        };
    })();

    return (
        <CommandHeaderShell
            testId="doctor-patient-command-header"
            avatar={avatarNode}
            identityLabel="Patient"
            name={patient.name || "Unnamed patient"}
            nameTitle={patient.name}
            demographics={demographics}
            demographicsSuffix={demographicsSuffix}
            assignments={[
                {
                    label: "Primary clinic",
                    value: patient.primary_clinic_name || "—",
                    icon: Buildings,
                    testid: "patient-header-clinic",
                    title: patient.primary_clinic_name,
                },
                // iter101.88 — Contact assignment removed. The phone now
                // renders as an inline dial-link right under the Patient ID
                // (see demographicsSuffix). Keeps the header from carrying
                // the same info twice.
            ]}
            rightSlot={rightSlot}
            chips={[
                {
                    icon: Hash,
                    label: "Patient ID",
                    value: idStr,
                    mono: true,
                    testid: "patient-chip-id",
                    title: idStr,
                },
                {
                    icon: CalendarBlank,
                    label: "Added",
                    value: fmtDate(patient.created_at),
                    testid: "patient-chip-added",
                },
                {
                    icon: Folders,
                    label: "Total cases",
                    value: String(casesCount),
                    testid: "patient-chip-cases-count",
                    title: `${casesCount} case${casesCount === 1 ? "" : "s"} started with this patient`,
                },
                {
                    icon: HeartStraight,
                    label: "Latest case",
                    value: humanCaseStatus(latestStatus),
                    tone: statusTone(latestStatus),
                    testid: "patient-chip-latest-status",
                    title: latestStatus
                        ? `Most recent case is in status: ${humanCaseStatus(latestStatus)}`
                        : "No case has been started for this patient yet",
                },
                // iter101.89 — Compliance chip now reflects clinical
                // adherence (On time / Needs attention / At risk / —)
                // sourced from wear-log signals via
                // /doctor/patients/{id}. Replaces the earlier iter101.88
                // Invisique-vs-Self mapping which misread the design.
                complianceChip,
                {
                    /* iter101.92 — "Balance" was ambiguous (which
                       direction does the money flow?). Renamed to
                       "Patient owes" so it's crystal-clear: this is
                       what the patient still owes the doctor's clinic. */
                    icon: ReceiptX,
                    label: "Patient owes",
                    value: balance > 0 ? fmtINR(balance) : "Nil",
                    tone: balance > 0 ? "rose" : "emerald",
                    testid: "patient-chip-balance",
                    title: balance > 0
                        ? `${fmtINR(balance)} still due from the patient for treatment`
                        : "Patient is fully paid — nothing outstanding",
                },
            ]}
        />
    );
}
